import ApiClient from './api';
import { 
  startOfWeek, 
  endOfWeek, 
  startOfDay, 
  endOfDay, 
  format, 
  parseISO,
  addMinutes,
  isWithinInterval
} from 'date-fns';

/**
 * Calendar service for business owner calendar management
 */
class CalendarService {
  constructor() {
    this.api = new ApiClient();
  }

  /**
   * Get events for a specific date range
   * @param {string} customerId - Customer ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Object>} Events map by customer and calendar: {customerId: {business_name, calendars: {calendarName: {data}}}}
   */
  async getEvents(customerId, startDate, endDate) {
    try {
      const response = await this.api.get(
        `/api/v1/calendar/events/${customerId}/*`,
        {
          params: {
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd'),
          },
        }
      );
      // Return nested structure: {customerId: {business_name, calendars}}
      return response.customers || {};
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      throw error;
    }
  }

  /**
   * Get events for current week
   * @param {string} customerId - Customer ID
   * @param {Date} referenceDate - Reference date (defaults to today)
   * @returns {Promise<Object>} Events map by calendar
   */
  async getWeekEvents(customerId, referenceDate = new Date()) {
    const start = startOfWeek(referenceDate, { weekStartsOn: 1 }); // Monday
    const end = endOfWeek(referenceDate, { weekStartsOn: 1 });
    return this.getEvents(customerId, start, end);
  }

  /**
   * Get events for a specific day
   * @param {string} customerId - Customer ID
   * @param {Date} date - Target date
   * @returns {Promise<Object>} Events map by calendar
   */
  async getDayEvents(customerId, date = new Date()) {
    const start = startOfDay(date);
    const end = endOfDay(date);
    return this.getEvents(customerId, start, end);
  }

  /**
   * Check if a time slot is available
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {Date} slotStart - Slot start time
   * @param {Date} slotEnd - Slot end time
   * @returns {Promise<boolean>} True if available
   */
  async isSlotAvailable(customerId, calendarName, slotStart, slotEnd) {
    try {
      const events = await this.getEvents(
        customerId,
        startOfDay(slotStart),
        endOfDay(slotStart)
      );

      const calendarEvents = events[calendarName]?.calendar_events || [];
      
      // Check for overlaps
      for (const event of calendarEvents) {
        const eventStart = parseISO(event.start.dateTime);
        const eventEnd = parseISO(event.end.dateTime);
        
        // Check if new slot overlaps with existing event
        if (
          (slotStart >= eventStart && slotStart < eventEnd) ||
          (slotEnd > eventStart && slotEnd <= eventEnd) ||
          (slotStart <= eventStart && slotEnd >= eventEnd)
        ) {
          return false; // Overlap found
        }
      }
      
      return true; // No overlap, slot is available
    } catch (error) {
      console.error('Error checking slot availability:', error);
      return false;
    }
  }

  /**
   * Book a new meeting
   * @param {Object} bookingData - Booking details (includes customer_id)
   * @returns {Promise<Object>} Booking response
   */
  async bookMeeting(bookingData) {
    try {
      const response = await this.api.post('/api/v1/calendar/book', bookingData, {
        clientId: bookingData.customer_id
      });
      return response;
    } catch (error) {
      console.error('Error booking meeting:', error);
      throw error;
    }
  }

  /**
   * Cancel a meeting
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Cancellation response
   */
  async cancelMeeting(customerId, calendarName, eventId) {
    try {
      const response = await this.api.post('/api/v1/calendar/delete', {
        customer_id: customerId,
        calendar_name: calendarName,
        event_id: eventId,
      });
      return response;
    } catch (error) {
      console.error('Error canceling meeting:', error);
      throw error;
    }
  }

  /**
   * Reschedule a meeting
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {string} eventId - Event ID
   * @param {Date} newStart - New start time
   * @param {Date} newEnd - New end time
   * @param {string} timezone - Timezone
   * @returns {Promise<Object>} Reschedule response
   */
  async rescheduleMeeting(customerId, calendarName, eventId, newStart, newEnd, timezone = 'UTC') {
    try {
      const response = await this.api.post('/api/v1/calendar/rebook', {
        customer_id: customerId,
        calendar_name: calendarName,
        event_id: eventId,
        new_slot_start: newStart.toISOString(),
        new_slot_end: newEnd.toISOString(),
        timezone,
      });
      return response;
    } catch (error) {
      console.error('Error rescheduling meeting:', error);
      throw error;
    }
  }

  /**
   * Parse event data for calendar display
   * @param {Object} event - Raw event from API
   * @returns {Object} Parsed event
   */
  /**
   * Extract meeting type from description or notes
   * @param {string} description - Event description
   * @returns {string} Meeting type key
   */
  extractMeetingType(description) {
    if (!description) return '';
    
    // Look for meeting type indicators in the description
    // Common patterns: "Type: consultation", "Meeting type: follow-up", etc.
    const lines = description.split('\n');
    
    // Check each line for meeting type information
    for (const line of lines) {
      // Skip customer ID and contact info lines
      if (line.includes('Customer ID:') || 
          line.includes('@') || 
          line.match(/^\+?\d+/)) {
        continue;
      }
      
      // The remaining non-empty line might be the meeting notes/type
      const trimmed = line.trim();
      if (trimmed) {
        // This could be meeting type information
        // For now, return empty - we need to match against calendar config
        return '';
      }
    }
    
    return '';
  }

  parseEvent(event) {
    // Handle different API response formats
    // Sometimes the API returns start/end as strings directly, sometimes as objects
    let startStr, endStr;
    
    if (typeof event.start === 'string') {
      startStr = event.start;
    } else if (event.start?.dateTime || event.start?.date) {
      startStr = event.start.dateTime || event.start.date;
    }
    
    if (typeof event.end === 'string') {
      endStr = event.end;
    } else if (event.end?.dateTime || event.end?.date) {
      endStr = event.end.dateTime || event.end.date;
    }
    
    if (!startStr || !endStr) {
      console.error('Event missing start or end time:', event);
      return null;
    }
    
    const meetingTypeKey = event.meeting_type || 
                           event.type || 
                           event.extendedProperties?.private?.meeting_type ||
                           event.extendedProperties?.shared?.meeting_type ||
                           '';
    const meetingTypeDescription = event.meeting_type_description || '';
    
    const parsedEvent = {
      id: event.id || event.event_id,
      summary: event.summary || 'Untitled Event',
      start: parseISO(startStr),
      end: parseISO(endStr),
      description: event.description || '',
      customerName: event.customer_name || this.extractCustomerName(event.summary),
      customerPhone: this.extractPhone(event.description),
      customerEmail: this.extractEmail(event.description),
      notes: this.extractNotes(event.description),
      // type is the technical meeting_type key used for logic (e.g. booking)
      type: meetingTypeKey,
      // typeDescription is the human-readable meeting type description for UI display
      typeDescription: meetingTypeDescription || meetingTypeKey || event.summary || '',
      isCustomerMeeting: event.is_customer_meeting || false,
      confirmationStatus: event.confirmation_status || 
                         event.extendedProperties?.private?.confirmation_status ||
                         '',
    };
    
    return parsedEvent;
  }

  /**
   * Extract customer name from event summary
   * @param {string} summary - Event summary
   * @returns {string} Customer name
   */
  extractCustomerName(summary) {
    if (!summary) return '';
    // Event summaries are typically formatted as "Customer Name"
    // Remove any meeting type prefix if present
    return summary.split('-')[0].trim();
  }

  /**
   * Extract phone number from event description
   * @param {string} description - Event description
   * @returns {string} Phone number
   */
  extractPhone(description) {
    if (!description) return '';
    const lines = description.split('\n');
    return lines[0] || '';
  }

  /**
   * Extract email from event description
   * @param {string} description - Event description
   * @returns {string} Email
   */
  extractEmail(description) {
    if (!description) return '';
    const lines = description.split('\n');
    return lines[1] || '';
  }

  /**
   * Extract notes from event description
   * @param {string} description - Event description
   * @returns {string} Notes
   */
  extractNotes(description) {
    if (!description) return '';
    const lines = description.split('\n');
    return lines.slice(2).join('\n');
  }

  /**
   * Reschedule an existing meeting
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {string} eventId - Event ID to reschedule
   * @param {string} newStartTime - New start time (ISO format)
   * @param {string} newEndTime - New end time (ISO format)
   * @returns {Promise<Object>} Updated event
   */
  async rescheduleMeeting(customerId, calendarName, eventId, newStartTime, newEndTime) {
    try {
      const response = await this.api.post('/api/v1/calendar/rebook', {
        customer_id: customerId,
        calendar_name: calendarName,
        event_id: eventId,
        new_slot_start: newStartTime,
        new_slot_end: newEndTime,
      });
      return response;
    } catch (error) {
      console.error('Error rescheduling meeting:', error);
      throw error;
    }
  }

  /**
   * Generate time slots for a day based on business hours and granularity
   * @param {Date} date - Target date
   * @param {Object} businessHours - Business hours config
   * @param {number} granularity - Slot duration in minutes
   * @returns {Array} Array of time slots
   */
  generateTimeSlots(date, businessHours, granularity = 30) {
    const slots = [];
    const startTime = businessHours?.start_time || '09:00';
    const endTime = businessHours?.end_time || '17:00';
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let current = new Date(date);
    current.setHours(startHour, startMin, 0, 0);
    
    const end = new Date(date);
    end.setHours(endHour, endMin, 0, 0);
    
    while (current < end) {
      slots.push(new Date(current));
      current = addMinutes(current, granularity);
    }
    
    return slots;
  }
}

export const calendarService = new CalendarService();
export default CalendarService;
